import React, { useState } from 'react';
import { Plus, Trash2, Star } from 'lucide-react';
import PaintService from '../../../services/PaintService';
import ImageCarousel from '../ImageCarousel';
import { ConfirmationModal, toast } from '../V2Components';
import { PLACEHOLDER_IMAGES } from '../../../constants/images';
import { useFilePicker } from '../../../hooks/useFilePicker';

// Mirror of api/paints.py MAX_PAINT_IMAGES — keep in sync. The frontend cap is
// a UX guard (disables the add button, shows the count); the backend stays the
// source of truth and rejects over-limit uploads with a 400.
const MAX_PAINT_IMAGES = 5;

/**
 * Image management for the Paint edit page. Wraps the read-only ImageCarousel
 * with upload, set-primary, and delete controls so users can curate up to 5
 * label/swatch photos per paint.
 *
 * Mirrors components/v2/RoomDetails/RoomImageManager — same busy-state guard,
 * sequential upload pattern, and thumbnail-strip layout. Only the entity name,
 * service module, and cap differ.
 *
 * @param {number} paintId - Paint ID images belong to
 * @param {Array} images - Current paint images (PaintImageSchema: id, url, is_primary, original_filename)
 * @param {Function} onChange - Called after a successful mutation so the parent can refetch
 */
const PaintImageManager = ({ paintId, images = [], onChange }) => {
    const [uploading, setUploading] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [settingPrimaryId, setSettingPrimaryId] = useState(null);

    const isFull = images.length >= MAX_PAINT_IMAGES;
    // Any in-flight mutation. Used to disable controls so the user can't fire
    // overlapping requests (e.g. set-primary while an upload is still running)
    // that would race against each other and the onChange refetch.
    const busy = uploading || deleting || settingPrimaryId !== null;

    const handleUpload = async (files) => {
        if (!files || files.length === 0) return;
        const remaining = MAX_PAINT_IMAGES - images.length;
        if (remaining <= 0) {
            toast.error(`A paint can have at most ${MAX_PAINT_IMAGES} photos`);
            return;
        }
        const toUpload = Array.from(files).slice(0, remaining);
        if (toUpload.length < files.length) {
            toast.error(`Only ${remaining} more photo${remaining === 1 ? '' : 's'} can be added`);
        }

        setUploading(true);
        // Sequential uploads — a failure on file N still leaves files 1..N-1
        // persisted on the backend. Track per-file success and always refetch
        // if anything landed, otherwise successful photos stay invisible until
        // a manual page refresh.
        let succeeded = 0;
        let lastError = null;
        try {
            for (const f of toUpload) {
                try {
                    await PaintService.uploadPaintImage(paintId, f);
                    succeeded += 1;
                } catch (err) {
                    lastError = err;
                }
            }
        } finally {
            if (succeeded > 0) onChange?.();
            setUploading(false);
        }

        const failed = toUpload.length - succeeded;
        if (failed === 0) {
            toast.success(`${succeeded} photo${succeeded === 1 ? '' : 's'} uploaded`);
        } else if (succeeded > 0) {
            toast.error(
                `Uploaded ${succeeded} photo${succeeded === 1 ? '' : 's'}, ${failed} failed`
                + (lastError?.message ? `: ${lastError.message}` : '')
            );
        } else {
            toast.error(lastError?.message || 'Failed to upload photo');
        }
    };

    // OS-native file chooser. See hooks/useFilePicker for the iOS rationale.
    // maxSizeMB matches the backend's 10MB cap in api/paints.py upload_paint_image.
    const { triggerPicker: openImagePicker, fileInputElement: imageFileInput } = useFilePicker({
        kind: 'image',
        multiple: true,
        maxSizeMB: 10,
        onFiles: handleUpload,
        disabled: isFull || busy,
    });

    const handleSetPrimary = async (image) => {
        if (busy) return; // re-entrancy guard against rapid double-clicks
        setSettingPrimaryId(image.id);
        try {
            await PaintService.setPaintImagePrimary(paintId, image.id);
            toast.success('Primary photo updated');
            onChange?.();
        } catch (err) {
            toast.error(err.message || 'Failed to set primary photo');
        } finally {
            setSettingPrimaryId(null);
        }
    };

    const handleDeleteConfirmed = async () => {
        if (!imageToDelete) return;
        setDeleting(true);
        try {
            await PaintService.deletePaintImage(paintId, imageToDelete.id);
            toast.success('Photo deleted');
            onChange?.();
            setImageToDelete(null);
        } catch (err) {
            toast.error(err.message || 'Failed to delete photo');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="flex flex-col p-4 gap-4">
            {/* Header — Add Photo button + photo count */}
            <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-[var(--text-muted)]">
                    {images.length}/{MAX_PAINT_IMAGES} photos
                </span>
                {imageFileInput}
                <button
                    type="button"
                    disabled={isFull || busy}
                    onClick={openImagePicker}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--primary)] text-[var(--primary-text)] rounded-full text-xs font-bold shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    aria-label="Add photo"
                >
                    {uploading ? (
                        <>
                            <span className="animate-spin rounded-full h-3 w-3 border-b border-current" />
                            Uploading...
                        </>
                    ) : isFull ? (
                        'Max photos reached'
                    ) : (
                        <>
                            <Plus size={14} />
                            Add Photo
                        </>
                    )}
                </button>
            </div>

            {/* Carousel viewer — narrower than rooms because paint cards are
                portrait-oriented; the 4:5 aspect makes a vertical swatch read
                well on both the edit page and the library grid. */}
            <div className="flex items-center justify-center">
                <ImageCarousel
                    images={images}
                    aspectRatio="aspect-[4/5]"
                    className="w-full max-w-xs"
                />
            </div>

            {/* Thumbnail strip with per-photo manage actions. Only shown when
                there are photos — an empty paint leans on the carousel's own
                placeholder + the Add Photo button above. */}
            {images.length > 0 && (
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                    {images.map((img) => (
                        <div
                            key={img.id}
                            className="relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-[var(--border)] bg-[var(--bg-card)]"
                        >
                            <img
                                src={img.url || PLACEHOLDER_IMAGES.room}
                                alt={img.original_filename || 'Paint photo'}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    // Fall back to the placeholder if a presigned URL
                                    // has expired / 404s. Clear the handler first so
                                    // the placeholder can't loop.
                                    if (e.currentTarget.src !== PLACEHOLDER_IMAGES.room) {
                                        e.currentTarget.onerror = null;
                                        e.currentTarget.src = PLACEHOLDER_IMAGES.room;
                                    }
                                }}
                            />

                            {/* Action controls are always visible (not hover-gated)
                                so they work on touch devices, which have no hover. */}
                            {img.is_primary ? (
                                <span
                                    className="absolute top-1 left-1 bg-[var(--primary)] text-[var(--primary-text)] rounded-full p-1 shadow"
                                    title="Primary photo"
                                >
                                    <Star size={11} fill="currentColor" />
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => handleSetPrimary(img)}
                                    title="Set as primary"
                                    aria-label="Set as primary"
                                    className="absolute top-1 left-1 bg-white/90 text-[var(--primary)] rounded-full p-1.5 shadow hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {settingPrimaryId === img.id ? (
                                        <span className="block animate-spin rounded-full h-[11px] w-[11px] border-b border-current" />
                                    ) : (
                                        <Star size={11} />
                                    )}
                                </button>
                            )}

                            <button
                                type="button"
                                disabled={busy}
                                onClick={() => setImageToDelete(img)}
                                title="Delete photo"
                                aria-label="Delete photo"
                                className="absolute top-1 right-1 bg-white/90 text-[var(--error)] rounded-full p-1.5 shadow hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Trash2 size={11} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmationModal
                isOpen={!!imageToDelete}
                onClose={() => setImageToDelete(null)}
                onConfirm={handleDeleteConfirmed}
                isLoading={deleting}
                title="Delete this photo?"
                message="Are you sure you want to delete this photo? This action cannot be undone."
                confirmText={deleting ? 'Deleting...' : 'Delete'}
                isDanger
                icon={Trash2}
            />
        </div>
    );
};

export default PaintImageManager;
